import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { User } from 'lucide-react';
import { toast } from 'sonner';
import {
  ACQUISITION_STAGE_BADGE,
  ACQUISITION_STAGE_LABEL,
  AVAILABILITY_BADGE,
  AVAILABILITY_LABEL,
  CLAIM_STATUS_BADGE,
  CLAIM_STATUS_LABEL,
  CONSENT_CHANNELS,
  LOSS_REASON_LABEL,
  LOSS_REASONS,
  OUTREACH_CHANNEL_LABEL,
  OUTREACH_OUTCOME_LABEL,
  SALES_GOAL_BADGE,
  SALES_GOAL_LABEL,
  SPEND_BAND_LABEL,
  SUBJECT_KIND_LABEL,
  formatGhs,
  sourceLabel,
  type ConsentChannel,
  type LossReason,
  type OutreachFeedbackOutcome,
  type OutreachFeedbackSentiment,
  type SpendBand,
  type SubjectKind,
} from '@/client/types/sales';
import { SalesPageShell } from './components/SalesPageShell';
import { LeadPerformancePanel } from './components/LeadPerformancePanel';
import { SubjectSettingsPanel } from './components/SubjectSettingsPanel';
import { ConversationThread } from './components/ConversationThread';
import { TagInput } from './components/TagInput';
import { CopyButton, CopyLinkButton, FitBars, Panel, StatusBadge, btnGhost, btnPrimary, compactGhs, selectCls, timeLeftLabel, toastSales } from './components/sales-ui';
import {
  useAdvanceClaimMutation,
  useAttachOfferMutation,
  useCommitClaimMutation,
  useLoseClaimMutation,
  useOutreachFeedbackMutation,
  usePatchSubjectMutation,
  useReleaseClaimMutation,
  useSalesClaimsQuery,
  useSalesMeQuery,
  useSalesOffersQuery,
  useSalesOutreachQuery,
  useSalesPolicyQuery,
  useSalesPurposesQuery,
  useSalesRepsQuery,
  useSalesSubjectQuery,
  useSalesSubjectThreadQuery,
  useWinClaimMutation,
} from '@/client/api/sales/sales';
import { DEFAULT_SALES_POLICY } from '@/client/types/sales';
import { achievedGoalsFor, availabilityFor, displayClaimStatus, fitScoreFor, lockingClaimFor } from './sales-live';
import { promptActAs, useCurrentRep } from './sales-store';

export default function SalesLeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const rep = useCurrentRep();
  const me = useSalesMeQuery().data;
  const subjectQ = useSalesSubjectQuery(id);
  const claimsQ = useSalesClaimsQuery({ limit: 200 });
  const outreachQ = useSalesOutreachQuery({ limit: 200 });
  const threadQ = useSalesSubjectThreadQuery(id);
  const purposesQ = useSalesPurposesQuery();
  const offersQ = useSalesOffersQuery();
  const policyQ = useSalesPolicyQuery();
  const repsQ = useSalesRepsQuery();
  const patchMut = usePatchSubjectMutation();
  const commitMut = useCommitClaimMutation();
  const offerMut = useAttachOfferMutation();
  const winMut = useWinClaimMutation();
  const loseMut = useLoseClaimMutation();
  const releaseMut = useReleaseClaimMutation();
  const advanceMut = useAdvanceClaimMutation();
  const feedbackMut = useOutreachFeedbackMutation();
  const subject = subjectQ.data;
  const claims = claimsQ.data?.claims ?? [];
  const outreaches = outreachQ.data?.outreach ?? [];
  const purposes = purposesQ.data ?? [];
  const offers = offersQ.data ?? [];
  const policy = policyQ.data ?? DEFAULT_SALES_POLICY;
  const reps = repsQ.data ?? [];
  const purposeById = (pid: string) => purposes.find((p) => p.id === pid);
  const getRep = (rid: string) => reps.find((r) => r.id === rid);
  const history = claims.filter((c) => c.subjectId === id);
  const active = id ? lockingClaimFor(id, claims) : undefined;
  const timeline = (threadQ.data?.events ?? []).slice().sort((a, b) => b.at.localeCompare(a.at));
  const achieved = id ? achievedGoalsFor(id, claims) : [];
  const subjectOutreaches = (outreachQ.data?.outreach ?? threadQ.data?.outreach ?? outreaches)
    .filter((o) => o.subjectId === id)
    .sort((a, b) => b.at.localeCompare(a.at));

  const [lossReason, setLossReason] = useState<LossReason>('not_a_fit');
  const [advancePurpose, setAdvancePurpose] = useState('');
  const [winUid, setWinUid] = useState('');
  const [winListing, setWinListing] = useState('');
  const [winPromo, setWinPromo] = useState('');
  const [offerId, setOfferId] = useState('');
  const [displayName, setDisplayName] = useState(subject?.displayName ?? '');
  const [phone, setPhone] = useState(subject?.phone ?? '');
  const [email, setEmail] = useState(subject?.email ?? '');
  const [kind, setKind] = useState<SubjectKind>(subject?.kind ?? 'person');
  const [spendBand, setSpendBand] = useState<SpendBand>(subject?.spendBand ?? 'unknown');
  const [region, setRegion] = useState(subject?.areas[0]?.region ?? '');
  const [notes, setNotes] = useState(subject?.profile.notes ?? '');
  const [tags, setTags] = useState<string[]>(subject?.tags ?? []);
  const [consent, setConsent] = useState(subject?.consent ?? {});
  const [dnc, setDnc] = useState(Boolean(subject?.doNotContact));
  const [uid, setUid] = useState(subject?.platform?.uid != null ? String(subject.platform.uid) : '');
  const [oid, setOid] = useState(subject?.platform?.oid != null ? String(subject.platform.oid) : '');
  const [fbOutcome, setFbOutcome] = useState<OutreachFeedbackOutcome>('interested');
  const [fbSentiment, setFbSentiment] = useState<OutreachFeedbackSentiment>('neutral');

  useEffect(() => {
    if (!subject) return;
    setDisplayName(subject.displayName);
    setPhone(subject.phone ?? '');
    setEmail(subject.email ?? '');
    setKind(subject.kind);
    setSpendBand(subject.spendBand);
    setRegion(subject.areas[0]?.region ?? '');
    setNotes(subject.profile.notes ?? '');
    setTags(subject.tags ?? []);
    setConsent(subject.consent);
    setDnc(subject.doNotContact);
    setUid(subject.platform?.uid != null ? String(subject.platform.uid) : '');
    setOid(subject.platform?.oid != null ? String(subject.platform.oid) : '');
  }, [subject?.id]);

  // Consent also changes from Contact settings (recording a source grants it);
  // keep the ticks current so Save profile never writes back a stale set.
  const consentKey = JSON.stringify(subject?.consent ?? {});
  useEffect(() => {
    if (subject) setConsent(subject.consent);
  }, [consentKey]);

  const purpose = active ? purposeById(active.purposeId) : undefined;
  const scoreGoal = purpose?.goal ?? 'signup';
  const nextPurposes = useMemo(
    () => purposes.filter((p) => p.active && !achieved.includes(p.goal)),
    [purposes, achieved],
  );
  const pending = subjectOutreaches.find((o) => o.direction === 'outbound' && !o.feedback && o.status !== 'draft' && o.status !== 'blocked');
  const ok = (msg: string) => ({
    onSuccess: () => toast.success(msg),
    onError: toastSales,
  });

  if (subjectQ.isLoading) {
    return (
      <SalesPageShell title="Prospect" description="Loading." crumbs={[{ label: 'Prospects', href: '/admin/sales/prospects/overview' }]} icon={User}>
        <p className="text-sm text-gray-500">Loading prospect.</p>
      </SalesPageShell>
    );
  }

  if (!subject) {
    return (
      <SalesPageShell title="Prospect" description="Not found." crumbs={[{ label: 'Prospects', href: '/admin/sales/prospects/overview' }]} icon={User}>
        <p className="text-sm text-gray-500">Unknown prospect.</p>
      </SalesPageShell>
    );
  }

  const av = availabilityFor(subject, claims);

  return (
    <SalesPageShell
      title={subject.displayName}
      description={`${SUBJECT_KIND_LABEL[subject.kind]} · ${subject.areas.map((a) => a.region).join(', ') || 'No area'}${purpose ? ` · ${purpose.name}` : ''}`}
      crumbs={[{ label: 'Prospects', href: '/admin/sales/prospects/overview' }, { label: subject.displayName }]}
      icon={User}
      actions={
        purpose ? (
          <div className="text-right">
            <div className="text-xl font-semibold tabular-nums text-gray-900">{compactGhs(purpose.estimatedValueGhs)}</div>
            <div className="text-[11px] text-gray-400">purpose value</div>
          </div>
        ) : undefined
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <StatusBadge label={AVAILABILITY_LABEL[av]} className={AVAILABILITY_BADGE[av]} />
        <StatusBadge label={ACQUISITION_STAGE_LABEL[subject.acquisitionStage]} className={ACQUISITION_STAGE_BADGE[subject.acquisitionStage]} />
        {achieved.map((g) => (
          <StatusBadge key={g} label={`Won ${SALES_GOAL_LABEL[g]}`} className={SALES_GOAL_BADGE[g]} />
        ))}
        <CopyLinkButton label="Copy prospect link" className="ml-auto" />
      </div>

      <div className="mb-4 grid gap-4 lg:grid-cols-3">
        <Panel title="Profile">
          <div className="space-y-2 text-sm text-gray-700">
            <div className="text-xs text-gray-500">{sourceLabel(subject.source)}</div>
            <label className="block text-xs text-gray-500">
              Name
              <input
                className={`${selectCls} mt-1 w-full`}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </label>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="block text-xs text-gray-500">
                <span className="inline-flex items-center gap-1">Phone <CopyButton value={phone} label="Copy number" /></span>
                <input className={`${selectCls} mt-1 w-full`} value={phone} onChange={(e) => setPhone(e.target.value)} />
              </label>
              <label className="block text-xs text-gray-500">
                <span className="inline-flex items-center gap-1">Email <CopyButton value={email} label="Copy email" /></span>
                <input className={`${selectCls} mt-1 w-full`} value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              <label className="block text-xs text-gray-500">
                Kind
                <select
                  className={`${selectCls} mt-1 w-full`}
                  value={kind}
                  onChange={(e) => setKind(e.target.value as SubjectKind)}
                >
                  {(Object.keys(SUBJECT_KIND_LABEL) as SubjectKind[]).map((k) => (
                    <option key={k} value={k}>
                      {SUBJECT_KIND_LABEL[k]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs text-gray-500">
                Spend
                <select
                  className={`${selectCls} mt-1 w-full`}
                  value={spendBand}
                  onChange={(e) => setSpendBand(e.target.value as SpendBand)}
                >
                  {(Object.keys(SPEND_BAND_LABEL) as SpendBand[]).map((b) => (
                    <option key={b} value={b}>
                      {SPEND_BAND_LABEL[b]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs text-gray-500 sm:col-span-2">
                Region
                <input className={`${selectCls} mt-1 w-full`} value={region} onChange={(e) => setRegion(e.target.value)} />
              </label>
              <label className="block text-xs text-gray-500">
                FeelyFeely uid
                <input className={`${selectCls} mt-1 w-full`} value={uid} onChange={(e) => setUid(e.target.value)} placeholder="91001" />
              </label>
              <label className="block text-xs text-gray-500">
                Org oid
                <input className={`${selectCls} mt-1 w-full`} value={oid} onChange={(e) => setOid(e.target.value)} placeholder="401" />
              </label>
            </div>
            <div>
              <p className="mb-1 text-xs text-gray-500">Consent to contact</p>
              <div className="flex flex-wrap gap-3">
                {CONSENT_CHANNELS.map((ch: ConsentChannel) => (
                  <label key={ch} className="flex items-center gap-1.5 text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={consent[ch] === true}
                      onChange={(e) => setConsent((c) => ({ ...c, [ch]: e.target.checked }))}
                    />
                    {OUTREACH_CHANNEL_LABEL[ch]}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-rose-800">
              <input type="checkbox" checked={dnc} onChange={(e) => setDnc(e.target.checked)} />
              Do not contact
            </label>
            <textarea
              className={`${selectCls} w-full`}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes the floor can add to"
            />
            <div>
              <label htmlFor="lead-tags" className="mb-1 block text-xs text-gray-500">
                Tags
              </label>
              <TagInput id="lead-tags" value={tags} onChange={setTags} />
            </div>
            {subject.profile.autoFilled.length > 0 && (
              <p className="text-[11px] text-gray-400">Auto-filled: {subject.profile.autoFilled.join(', ')}</p>
            )}
            {subject.profile.manualOverrides.length > 0 && (
              <p className="text-[11px] text-gray-400">Edited: {subject.profile.manualOverrides.join(', ')}</p>
            )}
            {rep ? (
              <button
                type="button"
                className={btnPrimary}
                onClick={() =>
                  patchMut.mutate(
                    {
                      id: subject.id,
                      displayName,
                      phone,
                      email,
                      kind,
                      spendBand,
                      areas: region ? [{ region }] : [],
                      notes,
                      tags,
                      consent,
                      doNotContact: dnc,
                      platform: {
                        uid: uid.trim() ? Number(uid) : null,
                        oid: oid.trim() ? Number(oid) : null,
                      },
                    },
                    ok('Profile saved'),
                  )
                }
              >
                Save profile
              </button>
            ) : (
              <p className="text-xs text-gray-400">
                <button type="button" className="font-medium text-blue-700 hover:underline" onClick={() => promptActAs('edit this record')}>
                  Choose who you are acting as
                </button>{' '}
                to edit this record.
              </p>
            )}
          </div>
        </Panel>

        <Panel title="Fit">
          <FitBars
            parts={subject.fitParts}
            weights={policy.fitWeights}
            goalGap={achieved.includes(scoreGoal) ? 0 : 1}
            score={fitScoreFor(subject, scoreGoal, claims, policy.fitWeights)}
          />
        </Panel>

        <Panel title="Current claim">
          {active && purpose ? (
            <div className="space-y-2 text-sm">
              <StatusBadge label={CLAIM_STATUS_LABEL[displayClaimStatus(active)]} className={CLAIM_STATUS_BADGE[displayClaimStatus(active)]} />
              <div className="text-lg font-semibold tabular-nums text-gray-900">{formatGhs(purpose.estimatedValueGhs)}</div>
              <div>
                {purpose.name} · {SALES_GOAL_LABEL[purpose.goal]}
              </div>
              <div className="text-xs text-gray-500">Owner {getRep(active.ownerId)?.name}</div>
              <div className="text-xs text-gray-500">{timeLeftLabel(active.expiresAt, Date.now())}</div>
            </div>
          ) : (
            <p className="text-sm text-gray-400">
              No active claim.{' '}
              <Link to="/admin/sales/prospects" className="text-blue-700 hover:underline">
                Open pool
              </Link>
            </p>
          )}
        </Panel>
      </div>

      <div className="mb-4">
        <LeadPerformancePanel subject={subject} />
      </div>

      {(me?.canWriteProspects || me?.isSuper) && (
        <SubjectSettingsPanel subject={subject} canManage={Boolean(me?.canManageSettings || me?.isSuper)} />
      )}

      <Panel
        title="Thread"
        className="mb-4"
        actions={
          <div className="flex gap-4">
            <Link to={`/admin/sales/communications?subject=${subject.id}`} className="text-sm text-blue-700 hover:underline">
              Message
            </Link>
            <Link to={`/admin/sales/outreach/${subject.id}`} className="text-sm text-blue-700 hover:underline">
              Open in Outreach
            </Link>
          </div>
        }
      >
        <ConversationThread items={subjectOutreaches} prospectName={subject.displayName} />
        {pending && rep ? (
            <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
              <select className={selectCls} value={fbSentiment} onChange={(e) => setFbSentiment(e.target.value as OutreachFeedbackSentiment)}>
                <option value="positive">Positive</option>
                <option value="neutral">Neutral</option>
                <option value="negative">Negative</option>
              </select>
              <select className={selectCls} value={fbOutcome} onChange={(e) => setFbOutcome(e.target.value as OutreachFeedbackOutcome)}>
                {(Object.keys(OUTREACH_OUTCOME_LABEL) as OutreachFeedbackOutcome[]).map((k) => (
                  <option key={k} value={k}>
                    {OUTREACH_OUTCOME_LABEL[k]}
                  </option>
                ))}
              </select>
              <button
                className={btnGhost}
                onClick={() =>
                  feedbackMut.mutate({ id: pending.id, sentiment: fbSentiment, outcome: fbOutcome }, ok('Feedback saved'))
                }
              >
                Save feedback
              </button>
            </div>
        ) : null}
      </Panel>

      {active && rep && active.ownerId === rep.id && (
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <Panel title="Move the claim" className="lg:col-span-2">
            <div className="flex flex-wrap gap-2">
              <button className={btnGhost} onClick={() => commitMut.mutate({ id: active.id }, ok('Committed'))}>
                Mark committed
              </button>
              <select className={selectCls} value={offerId} onChange={(e) => setOfferId(e.target.value)}>
                <option value="">Attach offer…</option>
                {offers.filter((o) => o.active).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              <button
                className={btnGhost}
                disabled={!offerId}
                onClick={() => offerMut.mutate({ id: active.id, offerId }, ok('Offer attached'))}
              >
                Attach
              </button>
            </div>
          </Panel>
          <Panel title="Close">
            <div className="space-y-3">
              {purpose?.goal === 'signup' && (
                <input className={`${selectCls} w-full`} value={winUid} onChange={(e) => setWinUid(e.target.value)} placeholder="uid" />
              )}
              {purpose?.goal === 'supply' && (
                <input className={`${selectCls} w-full`} value={winListing} onChange={(e) => setWinListing(e.target.value)} placeholder="listing id" />
              )}
              {purpose?.goal === 'monetize' && (
                <input className={`${selectCls} w-full`} value={winPromo} onChange={(e) => setWinPromo(e.target.value)} placeholder="promotion id" />
              )}
              <button
                className={`${btnPrimary} w-full justify-center`}
                // Require the real evidence id for this goal before a win can be
                // recorded — no bogus wins from blank/placeholder values.
                disabled={
                  !purpose ||
                  (purpose.goal === 'signup' && !(winUid.trim() && Number.isFinite(Number(winUid)))) ||
                  (purpose.goal === 'supply' && !winListing.trim()) ||
                  (purpose.goal === 'monetize' && !winPromo.trim())
                }
                onClick={() =>
                  winMut.mutate(
                    {
                      id: active.id,
                      evidence: {
                        uid: purpose?.goal === 'signup' ? Number(winUid) : undefined,
                        listingId: purpose?.goal === 'supply' ? winListing.trim() : undefined,
                        promotionId: purpose?.goal === 'monetize' ? winPromo.trim() : undefined,
                      },
                    },
                    ok('Won'),
                  )
                }
              >
                Mark won
              </button>
              <div className="flex gap-2">
                <select className={`${selectCls} flex-1`} value={lossReason} onChange={(e) => setLossReason(e.target.value as LossReason)}>
                  {LOSS_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {LOSS_REASON_LABEL[r]}
                    </option>
                  ))}
                </select>
                <button
                  className={btnGhost}
                  onClick={() => {
                    const finish = () => loseMut.mutate({ id: active.id, reason: lossReason }, ok('Lost'));
                    if (pending) {
                      feedbackMut.mutate({ id: pending.id, sentiment: fbSentiment, outcome: fbOutcome }, { onSuccess: finish, onError: toastSales });
                    } else finish();
                  }}
                >
                  Lose
                </button>
              </div>
              <button
                className={`${btnGhost} w-full justify-center`}
                onClick={() => {
                  const finish = () => releaseMut.mutate({ id: active.id }, ok('Released'));
                  if (pending) {
                    feedbackMut.mutate({ id: pending.id, sentiment: fbSentiment, outcome: fbOutcome }, { onSuccess: finish, onError: toastSales });
                  } else finish();
                }}
              >
                Release lock
              </button>
            </div>
          </Panel>
        </div>
      )}

      {!active && history.some((c) => c.status === 'won' && rep && c.ownerId === rep.id) && nextPurposes.length > 0 && (
        <Panel title="Advance" className="mt-4">
          <div className="flex flex-wrap gap-2">
            <select className={selectCls} value={advancePurpose} onChange={(e) => setAdvancePurpose(e.target.value)}>
              <option value="">Next purpose…</option>
              {nextPurposes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <button
              className={btnPrimary}
              disabled={!advancePurpose}
              onClick={() => {
                const won = history
                  .filter((c) => c.status === 'won' && rep && c.ownerId === rep.id)
                  .sort((a, b) => (b.win?.at ?? '').localeCompare(a.win?.at ?? ''))[0];
                if (!won) return;
                advanceMut.mutate({ id: won.id, body: { purposeId: advancePurpose } }, ok('Advanced — new claim opened'));
              }}
            >
              Advance
            </button>
          </div>
        </Panel>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel title="Claim history">
          <ul className="space-y-2 text-sm">
            {history.map((c) => {
              const p = purposeById(c.purposeId);
              return (
                <li key={c.id} className="flex items-center justify-between gap-2">
                  <span>
                    {p?.name} · {getRep(c.ownerId)?.name}
                  </span>
                  <StatusBadge label={CLAIM_STATUS_LABEL[displayClaimStatus(c)]} className={CLAIM_STATUS_BADGE[displayClaimStatus(c)]} />
                </li>
              );
            })}
          </ul>
        </Panel>
        <Panel title="Event log">
          <ul className="max-h-72 space-y-2 overflow-auto text-xs text-gray-600">
            {timeline.map((e) => (
              <li key={e.id}>
                <span className="font-medium text-gray-800">{e.type}</span> · {getRep(e.actorId)?.name ?? e.actorId} ·{' '}
                {new Date(e.at).toLocaleString()}
              </li>
            ))}
            {timeline.length === 0 && <li className="text-gray-400">No events yet.</li>}
          </ul>
        </Panel>
      </div>
    </SalesPageShell>
  );
}
